import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Check, MessageCircle } from "lucide-react";

import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { track } from "@/lib/analytics";
import { casosReales } from "@/lib/casos";
import { WHATSAPP } from "@/lib/contacto";

const canonical = "https://pymevisible.cl/casos";
const descripcion =
  "Conoce casos reales de negocios en Chile que usan stands NFC + QR de Pyme Visible para conectar a sus clientes con reseñas de Google.";

export const Route = createFileRoute("/casos")({
  component: CasosPage,
  head: () => ({
    meta: [
      { title: "Casos reales de stands NFC + QR en Chile | Pyme Visible" },
      { name: "description", content: descripcion },
      { property: "og:title", content: "Stands NFC + QR en negocios reales | Pyme Visible" },
      { property: "og:description", content: descripcion },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      {
        property: "og:image",
        content: "https://pymevisible.cl/clientes/stand-nfc-qr-coni-nails.jpg",
      },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${canonical}#webpage`,
              url: canonical,
              name: "Casos reales de stands NFC + QR",
              description: descripcion,
              isPartOf: { "@id": "https://pymevisible.cl/#website" },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Inicio",
                  item: "https://pymevisible.cl/",
                },
                { "@type": "ListItem", position: 2, name: "Casos reales", item: canonical },
              ],
            },
            {
              "@type": "ItemList",
              name: "Casos reales de clientes de Pyme Visible",
              itemListElement: casosReales.map((caso, indice) => ({
                "@type": "ListItem",
                position: indice + 1,
                name: caso.cliente,
                url: `${canonical}/${caso.slug}`,
              })),
            },
          ],
        }),
      },
    ],
  }),
});

function CasosPage() {
  return (
    <div className="pv-cases-page min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <section className="pv-page-hero border-b border-ink/10">
          <div className="mx-auto max-w-[1320px] px-5 py-10 sm:px-8 sm:py-20 lg:px-10">
            <nav
              aria-label="Ruta de navegación"
              className="pv-breadcrumb inline-flex flex-wrap items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-slate sm:text-sm"
            >
              <a href="/" className="hover:text-ink hover:underline">
                Inicio
              </a>
              <span aria-hidden="true" className="text-primary/60">
                /
              </span>
              <span className="text-ink">Casos reales</span>
            </nav>
            <p className="mt-7 font-mono text-xs uppercase tracking-[0.16em] text-primary">
              Trabajos comprobables
            </p>
            <h1 className="mt-3 max-w-5xl font-display text-5xl font-extrabold uppercase leading-[0.9] text-ink sm:text-7xl lg:text-8xl">
              Stands NFC + QR en negocios reales.
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate sm:text-xl">
              Fotografías de entregas realizadas por Pyme Visible. Cada caso muestra el producto, el
              uso acordado y cómo quedó instalado o presentado en el negocio.
            </p>
            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate">
              {["Fotos de entregas reales", "Configuración incluida", "Negocios en Chile"].map(
                (texto) => (
                  <li key={texto} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" aria-hidden="true" /> {texto}
                  </li>
                ),
              )}
            </ul>
          </div>
        </section>

        <section aria-labelledby="casos-titulo" className="pv-work-section">
          <div className="mx-auto max-w-[1320px] px-5 py-10 sm:px-8 sm:py-18 lg:px-10">
            <h2 id="casos-titulo" className="sr-only">
              Casos de clientes
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 lg:gap-5">
              {casosReales.map((caso, indice) => (
                <article
                  key={caso.slug}
                  className="pv-card pv-case-card group overflow-hidden rounded-[1.35rem] bg-white p-2 sm:rounded-[1.75rem] sm:p-3"
                >
                  <a
                    href={`/casos/${caso.slug}`}
                    className="block"
                    onClick={() =>
                      track("case_study_click", { cliente: caso.cliente, ubicacion: "casos" })
                    }
                  >
                    <div className="pv-case-media aspect-[5/4] overflow-hidden rounded-[1rem] bg-mist sm:rounded-[1.35rem]">
                      <ResponsiveImage
                        sizes="(max-width: 639px) 90vw, (max-width: 1023px) 44vw, 390px"
                        src={caso.imagen}
                        alt={caso.alt}
                        width={1200}
                        height={1500}
                        loading={indice < 2 ? "eager" : "lazy"}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.035]"
                      />
                    </div>
                    <div className="px-1 pb-2 pt-3 sm:px-2 sm:pb-3 sm:pt-4">
                      <p className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-primary">
                        {caso.rubro}
                      </p>
                      <h3 className="mt-1.5 font-display text-xl font-extrabold uppercase leading-none text-ink sm:text-2xl lg:text-3xl">
                        {caso.cliente}
                      </h3>
                      <p className="mt-2 hidden text-sm leading-relaxed text-slate sm:block">
                        {caso.resumen}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-ink sm:text-sm">
                        Ver caso completo{" "}
                        <ArrowRight
                          className="h-4 w-4 transition-transform group-hover:translate-x-1"
                          aria-hidden="true"
                        />
                      </span>
                    </div>
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="pv-dark-section text-white">
          <div className="mx-auto flex max-w-[1320px] flex-col items-start justify-between gap-6 px-5 py-12 sm:px-8 sm:py-16 lg:flex-row lg:items-center lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-signal">
                Tu negocio puede ser el próximo
              </p>
              <h2 className="mt-3 max-w-3xl font-display text-4xl font-extrabold uppercase leading-none sm:text-6xl">
                Cuéntanos qué enlace quieres conectar.
              </h2>
            </div>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { ubicacion: "casos_final" })}
              className="pv-button pv-button--primary inline-flex min-h-12 shrink-0 items-center gap-2 px-6 py-3.5"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Cotizar por WhatsApp
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
